import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { notificationSound } from "@/lib/notification-sound";
import { toast } from "sonner";

const ALERT_CHECK_INTERVAL = 60000; // Check every minute
const PENDING_THRESHOLD_MINUTES = 5; // Alert if pending for more than 5 minutes

/**
 * Hook that monitors pending print jobs and alerts if they've been waiting too long.
 * Useful for PC operators to know when QZ Tray might be disconnected.
 */
export function usePendingPrintJobsAlert(enabled: boolean) {
  const { activeRestaurant } = useRestaurantContext();
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastAlertTimeRef = useRef<number>(0);

  const checkPendingJobs = useCallback(async () => {
    if (!activeRestaurant?.id) return;
    
    // Skip check if offline
    if (!navigator.onLine) return;

    try {
      const thresholdTime = new Date();
      thresholdTime.setMinutes(thresholdTime.getMinutes() - PENDING_THRESHOLD_MINUTES);

      const { data: oldPendingJobs, error } = await supabase
        .from("pending_print_jobs")
        .select("id, order_number, created_at")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "pending")
        .lt("created_at", thresholdTime.toISOString());

      if (error) {
        // Silent handling for network errors
        if (error.message?.includes('Failed to fetch')) {
          return;
        }
        console.error("[PRINT-ALERT] Error checking pending jobs:", error);
        return;
      }

      if (oldPendingJobs && oldPendingJobs.length > 0) {
        // Only alert once every 5 minutes to avoid spam
        const now = Date.now();
        if (now - lastAlertTimeRef.current > PENDING_THRESHOLD_MINUTES * 60 * 1000) {
          lastAlertTimeRef.current = now;

          // Play warning sound
          await notificationSound.playPendingPrintJobs();

          // Show toast notification
          toast.warning(
            `${oldPendingJobs.length} impressão(ões) aguardando há mais de ${PENDING_THRESHOLD_MINUTES} minutos`,
            {
              description: "Verifique se o QZ Tray está conectado no computador.",
              duration: 10000,
            }
          );

          console.log(
            `[PRINT-ALERT] ${oldPendingJobs.length} jobs pending for more than ${PENDING_THRESHOLD_MINUTES} minutes`
          );
        }
      }
    } catch (error) {
      console.error("[PRINT-ALERT] Error in checkPendingJobs:", error);
    }
  }, [activeRestaurant?.id]);

  useEffect(() => {
    if (!enabled || !activeRestaurant?.id) return;

    // Check immediately on mount
    checkPendingJobs();

    // Setup periodic check
    checkIntervalRef.current = setInterval(checkPendingJobs, ALERT_CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [enabled, activeRestaurant?.id, checkPendingJobs]);

  return { checkPendingJobs };
}
